import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  Star, Filter, SortDesc, ChevronDown, X, Search,
  ArrowLeft, Calendar, Car, User, Users, RefreshCw
} from 'lucide-react';

const VEHICLE_TYPES = ['All', 'Sedan', 'SUV', 'Van', 'Truck', 'Motorcycle', 'Tuk-tuk'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'highest', label: 'Highest Rating' },
  { value: 'lowest', label: 'Lowest Rating' },
];

const QUICK_TAGS_MAP = {
  on_time: { label: 'On time', icon: '⏱️' },
  friendly: { label: 'Friendly', icon: '😊' },
  clean_vehicle: { label: 'Clean vehicle', icon: '✨' },
  safe_driving: { label: 'Safe driving', icon: '🛡️' },
  professional: { label: 'Professional', icon: '👔' },
  clear_instructions: { label: 'Clear instructions', icon: '📋' },
  good_tipper: { label: 'Good tipper', icon: '💰' },
};

const StarRating = ({ rating, size = 16 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5]?.map((s) => (
      <Star
        key={s}
        size={size}
        className={s <= rating ? 'text-[#ffd700] fill-[#ffd700]' : 'text-slate-300 fill-slate-300'}
      />
    ))}
  </div>
);

const ReviewerAvatar = ({ name, photoUrl, size = 40 }) => {
  const initials = name
    ? name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)
    : '?';
  const colors = ['bg-[#1a365d]', 'bg-[#2d5a87]', 'bg-[#c9a227]', 'bg-slate-600', 'bg-teal-700'];
  const colorIdx = name ? name?.charCodeAt(0) % colors?.length : 0;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`${name} profile photo`}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    );
  }
  return (
    <div
      className={`${colors?.[colorIdx]} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
};

const StarBreakdownBar = ({ starLevel, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-slate-600 w-4 text-right">{starLevel}</span>
      <Star size={12} className="text-[#ffd700] fill-[#ffd700] flex-shrink-0" />
      <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-[#ffd700] to-[#c9a227] transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-slate-500 w-8 text-right">{count}</span>
      <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
    </div>
  );
};

const RatingsReviewScreen = () => {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Sort & Filter state
  const [sortBy, setSortBy] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterStar, setFilterStar] = useState(0); // 0 = all
  const [filterRole, setFilterRole] = useState('all'); // all | customer | driver
  const [filterVehicle, setFilterVehicle] = useState('All');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('order_ratings')?.select(`
          id,
          order_id,
          reviewer_id,
          reviewer_role,
          rating,
          review_text,
          photo_url,
          created_at,
          user_profiles!reviewer_id (
            full_name,
            avatar_url
          ),
          orders!order_id (
            order_number,
            created_at,
            vehicle_type
          )
        `, { count: 'exact' });

      // Filters
      if (filterStar > 0) query = query?.eq('rating', filterStar);
      if (filterRole !== 'all') query = query?.eq('reviewer_role', filterRole);
      if (filterDateFrom) query = query?.gte('created_at', filterDateFrom);
      if (filterDateTo) query = query?.lte('created_at', filterDateTo + 'T23:59:59');
      if (searchText?.trim()) {
        query = query?.ilike('review_text', `%${searchText?.trim()}%`);
      }

      // Sort
      if (sortBy === 'newest') query = query?.order('created_at', { ascending: false });
      else if (sortBy === 'oldest') query = query?.order('created_at', { ascending: true });
      else if (sortBy === 'highest') query = query?.order('rating', { ascending: false })?.order('created_at', { ascending: false });
      else if (sortBy === 'lowest') query = query?.order('rating', { ascending: true })?.order('created_at', { ascending: false });

      const { data, error: err, count } = await query;
      if (err) throw err;

      let filtered = data || [];
      // Vehicle type filter (client-side since it's nested)
      if (filterVehicle !== 'All') {
        filtered = filtered?.filter(r =>
          r?.orders?.vehicle_type?.toLowerCase()?.includes(filterVehicle?.toLowerCase())
        );
      }

      setRatings(filtered);
      setTotalCount(count || 0);
    } catch (e) {
      console.error('Failed to fetch ratings:', e);
      setError('Failed to load ratings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, filterStar, filterRole, filterVehicle, filterDateFrom, filterDateTo, searchText]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // Compute breakdown from current data
  const breakdown = [5, 4, 3, 2, 1]?.map(star => ({
    star,
    count: ratings?.filter(r => r?.rating === star)?.length,
  }));
  const avgRating = ratings?.length > 0
    ? (ratings?.reduce((sum, r) => sum + r?.rating, 0) / ratings?.length)?.toFixed(1)
    : '0.0';

  const activeFilterCount = [
    filterStar > 0,
    filterRole !== 'all',
    filterVehicle !== 'All',
    filterDateFrom,
    filterDateTo,
  ]?.filter(Boolean)?.length;

  const clearFilters = () => {
    setFilterStar(0);
    setFilterRole('all');
    setFilterVehicle('All');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchText('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-KE', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#1a365d] shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium hidden sm:inline">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">On</span>
            <span className="text-[#ffd700] font-bold text-lg bg-white/10 px-2 py-0.5 rounded">TheGo</span>
            <span className="text-white/60 text-sm ml-2 hidden sm:inline">Ratings & Reviews</span>
          </div>
          <button
            onClick={fetchRatings}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Page Title */}
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Ratings & Reviews</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {totalCount > 0 ? `${totalCount} total review${totalCount !== 1 ? 's' : ''}` : 'All submitted ratings'}
          </p>
        </div>

        {/* Summary Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Average score */}
            <div className="flex flex-col items-center justify-center sm:border-r sm:border-slate-100 sm:pr-6 min-w-[120px]">
              <span className="text-5xl font-bold text-[#1a365d]">{avgRating}</span>
              <StarRating rating={Math.round(parseFloat(avgRating))} size={20} />
              <span className="text-xs text-slate-500 mt-1">{ratings?.length} reviews shown</span>
            </div>
            {/* Breakdown bars */}
            <div className="flex-1 space-y-2">
              {breakdown?.map(({ star, count }) => (
                <StarBreakdownBar
                  key={star}
                  starLevel={star}
                  count={count}
                  total={ratings?.length}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Search + Sort + Filter bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search review text..."
              value={searchText}
              onChange={e => setSearchText(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a365d]/20 focus:border-[#1a365d] transition-all"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <button
              onClick={() => { setShowSortMenu(p => !p); setShowFilters(false); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-[#1a365d] transition-colors whitespace-nowrap"
            >
              <SortDesc size={16} />
              {SORT_OPTIONS?.find(o => o?.value === sortBy)?.label}
              <ChevronDown size={14} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 min-w-[180px] overflow-hidden">
                {SORT_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => { setSortBy(opt?.value); setShowSortMenu(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      sortBy === opt?.value
                        ? 'bg-[#1a365d] text-white font-medium'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => { setShowFilters(p => !p); setShowSortMenu(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
              showFilters || activeFilterCount > 0
                ? 'bg-[#1a365d] text-white border-[#1a365d]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-[#1a365d]'
            }`}
          >
            <Filter size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-[#ffd700] text-[#1a365d] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#1a365d]">Filter Options</h3>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                >
                  <X size={12} /> Clear all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Star Rating Filter */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Star Rating</label>
                <div className="flex gap-1.5 flex-wrap">
                  {[0, 5, 4, 3, 2, 1]?.map(s => (
                    <button
                      key={s}
                      onClick={() => setFilterStar(s)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        filterStar === s
                          ? 'bg-[#1a365d] text-white border-[#1a365d]'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-[#1a365d]'
                      }`}
                    >
                      {s === 0 ? 'All' : (
                        <>
                          {s}
                          <Star size={10} className={filterStar === s ? 'fill-[#ffd700] text-[#ffd700]' : 'fill-slate-400 text-slate-400'} />
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reviewer Role */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Reviewer Role</label>
                <div className="flex gap-1.5">
                  {[
                    { value: 'all', label: 'All', icon: <Users size={12} /> },
                    { value: 'customer', label: 'Customer', icon: <User size={12} /> },
                    { value: 'driver', label: 'Driver', icon: <Car size={12} /> },
                  ]?.map(opt => (
                    <button
                      key={opt?.value}
                      onClick={() => setFilterRole(opt?.value)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        filterRole === opt?.value
                          ? 'bg-[#1a365d] text-white border-[#1a365d]'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-[#1a365d]'
                      }`}
                    >
                      {opt?.icon} {opt?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vehicle Type */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Vehicle Type</label>
                <select
                  value={filterVehicle}
                  onChange={e => setFilterVehicle(e?.target?.value)}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/20 focus:border-[#1a365d]"
                >
                  {VEHICLE_TYPES?.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Date Range</label>
                <div className="space-y-1.5">
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={e => setFilterDateFrom(e?.target?.value)}
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/20 focus:border-[#1a365d]"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={e => setFilterDateTo(e?.target?.value)}
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/20 focus:border-[#1a365d]"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
            <X size={16} className="flex-shrink-0" />
            {error}
            <button onClick={fetchRatings} className="ml-auto text-red-600 underline hover:no-underline">Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-10 h-10 border-3 border-[#1a365d] border-t-transparent rounded-full animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-slate-500">Loading reviews...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && ratings?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
              <Star size={32} className="text-slate-300" />
            </div>
            <p className="text-base font-semibold text-slate-600">No reviews found</p>
            <p className="text-sm text-slate-400">
              {activeFilterCount > 0 ? 'Try adjusting your filters' : 'No ratings have been submitted yet'}
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-[#1a365d] font-medium underline hover:no-underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Review Cards */}
        {!loading && ratings?.length > 0 && (
          <div className="space-y-3">
            {ratings?.map((review) => {
              const reviewerName = review?.user_profiles?.full_name || 'Anonymous';
              const tripDate = review?.orders?.created_at || review?.created_at;
              const vehicleType = review?.orders?.vehicle_type;
              const orderNum = review?.orders?.order_number;

              // Parse quick tags from review_text if stored as JSON prefix
              let parsedTags = [];
              let displayText = review?.review_text || '';
              try {
                if (displayText?.startsWith('{')) {
                  const parsed = JSON.parse(displayText);
                  parsedTags = parsed?.tags || [];
                  displayText = parsed?.text || '';
                }
              } catch {
                // plain text
              }

              return (
                <div
                  key={review?.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <ReviewerAvatar
                      name={reviewerName}
                      photoUrl={review?.user_profiles?.avatar_url || review?.photo_url}
                      size={44}
                    />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Top row */}
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[#1a365d] text-sm">{reviewerName}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              review?.reviewer_role === 'driver' ?'bg-[#1a365d]/10 text-[#1a365d]' :'bg-[#ffd700]/20 text-[#8a6d00]'
                            }`}>
                              {review?.reviewer_role === 'driver' ? '🚗 Driver' : '👤 Customer'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            <StarRating rating={review?.rating} size={14} />
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar size={11} />
                              {formatDate(tripDate)}
                            </span>
                            {vehicleType && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Car size={11} />
                                {vehicleType}
                              </span>
                            )}
                          </div>
                        </div>
                        {orderNum && (
                          <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded-lg font-mono flex-shrink-0">
                            #{orderNum}
                          </span>
                        )}
                      </div>

                      {/* Review text */}
                      {displayText && (
                        <p className="text-sm text-slate-700 mt-2 leading-relaxed">{displayText}</p>
                      )}

                      {/* Quick tag chips */}
                      {parsedTags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {parsedTags?.map(tag => {
                            const tagInfo = QUICK_TAGS_MAP?.[tag];
                            return tagInfo ? (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-1 rounded-full"
                              >
                                <span>{tagInfo?.icon}</span>
                                {tagInfo?.label}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}

                      {/* No text placeholder */}
                      {!displayText && parsedTags?.length === 0 && (
                        <p className="text-sm text-slate-400 italic mt-2">No written review</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer count */}
        {!loading && ratings?.length > 0 && (
          <p className="text-center text-xs text-slate-400 pb-4">
            Showing {ratings?.length} of {totalCount} review{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default RatingsReviewScreen;
